import { useState } from 'react';
import { formatRial } from '@kia/shared';
import { api } from '../api/client';
import { Button, useToast } from '../ui';

/**
 * The propose→confirm card — the ONE audited-write banner for an AI-proposed data-entry action, shared by
 * both AI screens (AiEntryChat's data-entry chat and AiChatScreen's analyst chat, AI-1). Extracted from the
 * single inline copy that used to live in AiEntryChat so the analyst chat can weld to the SAME confirm path
 * instead of cloning it (FLOW-2). The AI never writes: this card POSTs the proposal's own endpoint/payload
 * through the same validated + audited + RBAC route a human would — the human is the one who clicks «تأیید».
 */

/** Per-line margin-floor enrichment on a `deal` proposal (server-computed; the card only renders it). */
export interface ProposalLineFloor {
  productCode: string;
  name: string;
  price: number;
  fullCost: number;
  suggestedAtFloor: number;
  belowFloor: boolean;
}

/** Mirrors the server's EntryProposal (services/aiEntry.ts) — all nine proposable kinds + optional floors. */
export interface Proposal {
  kind:
    | 'customer'
    | 'supplier'
    | 'fx_rate'
    | 'journal'
    | 'cheque'
    | 'assumption'
    | 'price'
    | 'deal'
    | 'purchase_order';
  endpoint: string;
  payload: Record<string, unknown>;
  summaryFa: string;
  floors?: ProposalLineFloor[];
}

export function ProposalConfirm({
  proposal,
  onConfirmed,
  onCancel,
}: {
  proposal: Proposal;
  /** the write went through — the parent logs a ✓ line and clears the pending proposal. */
  onConfirmed: () => void;
  /** the user declined — the parent logs a «لغو» line and clears the pending proposal. */
  onCancel: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    const r = await api(proposal.endpoint, {
      method: 'POST',
      body: JSON.stringify(proposal.payload),
    });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ثبت', 'error');
      return;
    }
    toast('ثبت شد ✓');
    onConfirmed();
  }

  return (
    <div
      className="banner"
      style={{
        borderRight: '4px solid var(--amber)',
        background: 'var(--amber-bg)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <b style={{ flex: 1, minWidth: 200 }}>{proposal.summaryFa}</b>
      {/* deal-only: show each line's proposed price against its کف so a below-floor quote is caught here. */}
      {proposal.floors?.length ? (
        <ul style={{ flexBasis: '100%', margin: '4px 0 0', paddingRight: 18, fontSize: 13 }}>
          {proposal.floors.map((f, i) => (
            <li key={i} style={f.belowFloor ? { color: 'var(--danger)' } : undefined}>
              {f.name}: {formatRial(f.price)} · کف {formatRial(f.suggestedAtFloor)}
              {f.belowFloor ? ' ⚠ زیر کف' : ''}
            </li>
          ))}
        </ul>
      ) : null}
      <Button onClick={confirm} loading={busy}>
        تأیید و ثبت
      </Button>
      <Button variant="secondary" onClick={onCancel} disabled={busy}>
        لغو
      </Button>
    </div>
  );
}
